/**
 * lex.ivoliq StB-Cockpit (Phase 2, Juli 2026) — /advisor/lex
 * Für die Steuerberatungs-GmbH: Ampelstatus, Grenzen-Monitoring
 * (55k/100k/420k ab 2026), Fristenkalender (inkl. Quotenregelung) und
 * Anomalien-Inbox aller lex-Mandanten (AT-Rechtsanwälte).
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAdvisorAuth } from '@/contexts/AdvisorAuthContext';
import { Button } from '@/components/ui/button';
import KanzleiSecurityPanel from '@/components/lex/KanzleiSecurityPanel';
import LexEvzReminderWidget from '@/components/lex/LexEvzReminderWidget';
import LexOnboardingWizard from '@/components/lex/LexOnboardingWizard';
import {
  ArrowLeft, AlertTriangle, CalendarDays, Scale, ChevronRight, ShieldCheck, LogOut, Inbox, Check, Undo2, Mail, UserPlus,
} from 'lucide-react';
import '@/styles/lex.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
};

const AMPEL = {
  green: { dot: 'bg-emerald-500', label: 'Alles im grünen Bereich', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  yellow: { dot: 'bg-amber-500', label: 'Hinweise vorhanden', text: 'text-amber-700', bg: 'bg-amber-50' },
  red: { dot: 'bg-red-500', label: 'Handlungsbedarf', text: 'text-red-700', bg: 'bg-red-50' },
};

// Kurz-Labels für To-do-Badges in der kompakten Mandanten-Liste (Sep 2026)
const TODO_LABELS = {
  missing_tax_number: 'Steuernummer fehlt',
  missing_finanzonline: 'FON-Vollmacht fehlt',
  honorarnote_missing: 'Honorar-Vorlage fehlt',
  honorarnote_overdue: 'Honorarnote fällig',
  honorarnote_paused: 'Honorar pausiert',
  honorar_unpaid_overdue: 'Honorarnote unbezahlt',
  uva_overdue: 'UVA überfällig',
  uva_frequency_switch: 'UVA auf monatlich umstellen',
  revenue_jump: 'Umsatzsprung prüfen',
  threshold: 'Grenze prüfen',
};

export default function AdvisorLexCockpit() {
  const navigate = useNavigate();
  const location = useLocation();
  // Kanzlei-Modus: internes Portal der lex.ivoliq GmbH unter /kanzlei (kein Partner-SB-Kontext)
  const isKanzlei = location.pathname.startsWith('/kanzlei');
  const [showSecurity, setShowSecurity] = useState(false);
  const { advisor, loading: authLoading, logout } = useAdvisorAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inbox, setInbox] = useState({ entries: [], open_count: 0 });
  // Kanzlei-Onboarding (Sep 2026): Anwälte mit offener Einrichtung
  const [pending, setPending] = useState([]);
  const [onboardingClient, setOnboardingClient] = useState(null);

  const loadAll = () => {
    axios.get(`${API}/lex/advisor/cockpit`)
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API}/lex/advisor/inbox`)
      .then((res) => setInbox(res.data))
      .catch(() => {});
    axios.get(`${API}/lex/advisor/pending-onboarding`)
      .then((res) => setPending(res.data.pending || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (!advisor) return;
    loadAll();
  }, [advisor]);

  const setInboxStatus = async (id, status) => {
    try {
      await axios.put(`${API}/lex/advisor/inbox/${id}/status`, { status });
      setInbox((prev) => {
        const entries = prev.entries.map((e) => (e.id === id ? { ...e, status } : e));
        return { entries, open_count: entries.filter((e) => e.status === 'new').length };
      });
    } catch { /* noop */ }
  };

  if (authLoading) return null;
  if (!advisor && !authLoading) { navigate(isKanzlei ? '/kanzlei' : '/steuerberater'); return null; }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-[#C5A059]" />
      </div>
    );
  }

  const clients = data?.clients || [];
  const summary = data?.ampel_summary || {};
  const dataAccess = data?.data_access !== false;
  const anomalies = data?.anomalies || [];
  const deadlines = data?.deadlines || [];

  return (
    <div className="min-h-screen lex-theme" style={{ background: '#FDFCFB' }} data-testid="advisor-lex-cockpit">
      {/* Kopf */}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!isKanzlei && (
              <button onClick={() => navigate('/advisor/dashboard')}
                className="text-slate-500 hover:text-slate-800 transition-colors" data-testid="lex-cockpit-back-btn">
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <span className="lex-serif text-2xl font-semibold" style={{ color: '#0A1128' }}>
              lex<span style={{ color: '#C5A059' }}>.</span>ivoliq <span className="text-slate-400 font-normal text-lg">— {isKanzlei ? 'Kanzlei-Portal' : 'StB-Cockpit'}</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs uppercase tracking-widest font-semibold text-slate-400">{advisor?.name}</span>
            {isKanzlei && (
              <button onClick={() => setShowSecurity(true)}
                className="text-slate-400 hover:text-slate-700 transition-colors" title="Sicherheit" data-testid="kanzlei-security-btn">
                <ShieldCheck className="h-4 w-4" />
              </button>
            )}
            {isKanzlei && (
              <button onClick={async () => { try { await axios.post(`${API}/advisor/auth/logout`); } catch {} logout(); }}
                className="text-slate-400 hover:text-slate-700 transition-colors" title="Abmelden" data-testid="kanzlei-logout-btn">
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </header>

      {showSecurity && <KanzleiSecurityPanel onClose={() => setShowSecurity(false)} />}

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        {/* Vier-Augen-Datenzugriff (Sep 2026): ohne Freischaltung sind alle Daten maskiert */}
        {data && !dataAccess && (
          <div className="mb-6 border p-4 flex items-start gap-3"
            style={{ borderColor: '#C5A059', background: '#FBF7EF' }} data-testid="lex-no-access-banner">
            <ShieldCheck className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#92650B' }} />
            <div className="text-sm" style={{ color: '#5C4A1E' }}>
              <p className="font-semibold">Kein Mandantendaten-Zugriff — Finanz- und Steuerdaten maskiert.</p>
              <p className="text-xs mt-1">
                Aus Vertraulichkeitsgründen (Vier-Augen-Prinzip) sehen Sie nur die Namen der Mandanten —
                Honorarnoten, Beträge, Steuernummern und Details sind gesperrt. Ein Team-Kollege kann Ihren
                Zugriff unter <span className="font-semibold">Sicherheit → Team & Datenzugriff</span> freischalten.
              </p>
            </div>
          </div>
        )}
        {/* KPI-Zeile */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            ['Mandanten', clients.length, '#0A1128', 'lex-kpi-total'],
            ['Handlungsbedarf', summary.red || 0, '#DC2626', 'lex-kpi-red'],
            ['Hinweise', summary.yellow || 0, '#D97706', 'lex-kpi-yellow'],
            ['Anomalien offen', anomalies.length, '#C5A059', 'lex-kpi-anomalies'],
          ].map(([label, value, color, tid]) => (
            <div key={label} className="lex-card p-5" data-testid={tid}>
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</p>
              <p className="lex-mono text-3xl font-semibold mt-2" style={{ color }}>{value}</p>
            </div>
          ))}
        </div>

        {/* Offene Einrichtungen: Kanzlei kann das Onboarding stellvertretend durchführen */}
        {pending.length > 0 && (
          <div className="lex-card p-6 mb-10" style={{ borderLeft: '3px solid #C5A059' }} data-testid="lex-pending-onboarding-card">
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="h-4 w-4" style={{ color: '#C5A059' }} />
              <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-500">Offene Einrichtungen</h2>
              <span className="lex-mono text-xs font-semibold px-2 py-0.5" style={{ background: '#0A1128', color: '#C5A059' }}
                data-testid="lex-pending-count">{pending.length}</span>
            </div>
            <p className="text-sm text-slate-500 mb-3">
              Diese Anwälte sind registriert, haben die Einrichtung aber noch nicht abgeschlossen —
              Sie können das Onboarding stellvertretend durchführen.
            </p>
            {pending.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 flex-wrap border-t border-slate-100 py-3"
                data-testid={`lex-pending-row-${p.id}`}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#0A1128' }}>{p.name || p.email}</p>
                  <p className="text-xs text-slate-400 lex-mono">
                    {p.email}{p.email_verified === false ? ' · E-Mail noch unbestätigt' : ''}
                  </p>
                </div>
                <Button variant="outline" size="sm" className="rounded-none text-xs uppercase tracking-wider"
                  onClick={() => setOnboardingClient(p)} data-testid={`lex-onboard-client-btn-${p.id}`}>
                  Onboarding durchführen <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {clients.length > 0 && <LexEvzReminderWidget />}

        {clients.length === 0 ? (
          <div className="lex-card p-10 text-center" data-testid="lex-cockpit-empty">
            <Scale className="h-8 w-8 mx-auto mb-3" style={{ color: '#C5A059' }} />
            <p className="lex-serif text-2xl font-semibold" style={{ color: '#0A1128' }}>Noch keine lex-Mandanten verbunden.</p>
            <p className="text-sm text-slate-500 mt-2">Verbinden Sie Rechtsanwalts-Mandanten über den normalen Einladungs-Flow — sie erscheinen hier automatisch.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Mandanten-Liste */}
            <div className="lg:col-span-2 space-y-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-500">Mandanten</h2>
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/lex/advisor/uva/protokoll`, { responseType: 'blob' });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `UVA_Freigabe-Protokoll_${new Date().toISOString().slice(0, 10)}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch {
                      toast.error('Protokoll konnte nicht erzeugt werden.');
                    }
                  }}
                  className="text-[11px] uppercase tracking-widest font-semibold underline underline-offset-4 text-slate-500 hover:text-slate-800 transition-colors"
                  data-testid="lex-uva-protokoll-btn">
                  Freigabe-Protokoll (CSV)
                </button>
              </div>
              {clients.map((c) => {
                const todos = (c.anomalies || []).map((a) => TODO_LABELS[a.type] || null).filter(Boolean);
                const worst = Object.values(c.thresholds || {}).reduce((acc, t) => (!acc || t.pct > acc.pct ? t : acc), null);
                const worstColor = worst?.level === 'alert' ? '#DC2626' : worst?.level === 'warn' ? '#D97706' : '#94A3B8';
                const a = AMPEL[c.ampel] || AMPEL.green;
                return (
                  <button key={c.client_id} type="button"
                    onClick={() => {
                      if (!dataAccess) {
                        toast.error('Kein Mandantendaten-Zugriff — ein Team-Kollege muss Sie unter Sicherheit → Team & Datenzugriff freischalten.');
                        return;
                      }
                      navigate(`${isKanzlei ? '/kanzlei/mandant' : '/advisor/client'}/${c.client_id}?tab=mandat`);
                    }}
                    className="w-full text-left lex-card px-5 py-4 hover:shadow-md transition-shadow group block"
                    data-testid={`lex-client-row-${c.client_id}`}>
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${a.dot}`} title={a.label} data-testid={`lex-ampel-dot-${c.client_id}`} />
                      <div className="min-w-[170px] flex-1">
                        <p className="lex-serif text-lg font-semibold leading-tight" style={{ color: '#0A1128' }}>{c.anrede || c.name}</p>
                        <p className="text-[11px] text-slate-400 lex-mono">{c.tax_number ? `StNr. ${c.tax_number}` : 'Steuernummer fehlt'}</p>
                      </div>
                      <div className="text-sm min-w-[160px]">
                        <span className="text-slate-500 text-xs">UVA {c.ustva?.next_deadline_period || '—'}: </span>
                        <span className={`font-semibold text-xs ${c.ustva?.ustva_status === 'overdue' ? 'text-red-600' : c.ustva?.ustva_status === 'ready' ? 'text-emerald-600' : 'text-amber-600'}`}
                          data-testid={`lex-uva-status-${c.client_id}`}>
                          {c.ustva?.ustva_status_label || 'Keine UVA'}
                        </span>
                        {c.ustva?.next_deadline && (
                          <span className="text-slate-400 lex-mono text-[11px] block">fällig {fmtDate(c.ustva.next_deadline)}</span>
                        )}
                      </div>
                      {/* Mini-Indikator Grenzen (höchste Auslastung) */}
                      <div className="w-24 shrink-0" title={worst ? `${worst.label}: ${worst.pct} %` : ''}
                        data-testid={`lex-mini-th-${c.client_id}`}>
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Grenzen {worst ? `${Math.round(worst.pct)} %` : '—'}</p>
                        <div className="h-1 bg-slate-100 w-full">
                          <div className="h-1" style={{ width: `${Math.min(worst?.pct || 0, 100)}%`, background: worstColor }} />
                        </div>
                      </div>
                      {/* To-dos */}
                      <div className="flex flex-wrap gap-1.5 max-w-[280px]" data-testid={`lex-todos-${c.client_id}`}>
                        {todos.length === 0 ? (
                          <span className="lex-mono text-[11px] px-2 py-0.5 border"
                            style={{ borderColor: '#16A34A', color: '#166534', background: '#F0FDF4' }}>Keine To-dos ✓</span>
                        ) : (
                          <>
                            {todos.slice(0, 2).map((t, i) => (
                              <span key={i} className="lex-mono text-[11px] px-2 py-0.5 border"
                                style={{ borderColor: '#C5A059', color: '#92650B', background: '#FFFBEB' }}>{t}</span>
                            ))}
                            {todos.length > 2 && (
                              <span className="lex-mono text-[11px] px-2 py-0.5 border border-slate-200 text-slate-500 bg-slate-50">
                                +{todos.length - 2}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-600 transition-colors ml-auto shrink-0" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Rechte Spalte: Posteingang + Fristen + Anomalien */}
            <div className="space-y-8">
              <div className="lex-card p-6" data-testid="lex-kanzlei-inbox">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Inbox className="h-4 w-4" style={{ color: '#C5A059' }} />
                    <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-500">Posteingang</h2>
                  </div>
                  {inbox.open_count > 0 && (
                    <span className="lex-mono text-xs font-semibold px-2 py-0.5" style={{ background: '#0A1128', color: '#C5A059' }}
                      data-testid="lex-inbox-open-count">{inbox.open_count} offen</span>
                  )}
                </div>
                {inbox.entries.length === 0 ? (
                  <p className="text-sm text-slate-400" data-testid="lex-inbox-empty">Keine Mandanten-Anfragen.</p>
                ) : (
                  <div className="space-y-3">
                    {inbox.entries.slice(0, 10).map((e) => (
                      <div key={e.id}
                        className={`p-3 text-sm border-l-2 ${e.status === 'done' ? 'border-slate-200 bg-slate-50/60 opacity-60' : 'border-[#C5A059] bg-[#FBF7EF]'}`}
                        data-testid={`lex-inbox-entry-${e.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-slate-800">{e.subject}</p>
                          <span className="lex-mono text-[10px] text-slate-400 whitespace-nowrap">{fmtDate(e.created_at)}</span>
                        </div>
                        <p className="text-slate-600 mt-1 whitespace-pre-wrap break-words">{e.message}</p>
                        <p className="text-xs text-slate-400 mt-2 lex-mono">{e.user_name} · {e.ticket_number}</p>
                        <div className="flex gap-2 mt-2">
                          <a href={`mailto:${e.user_email}?subject=${encodeURIComponent(`Re: ${e.subject} · ${e.ticket_number}`)}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-600 hover:text-slate-900 transition-colors"
                            data-testid={`lex-inbox-reply-${e.id}`}>
                            <Mail className="h-3 w-3" /> Antworten
                          </a>
                          {e.status === 'done' ? (
                            <button onClick={() => setInboxStatus(e.id, 'new')}
                              className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800 transition-colors"
                              data-testid={`lex-inbox-reopen-${e.id}`}>
                              <Undo2 className="h-3 w-3" /> Wieder öffnen
                            </button>
                          ) : (
                            <button onClick={() => setInboxStatus(e.id, 'done')}
                              className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 transition-colors"
                              data-testid={`lex-inbox-done-${e.id}`}>
                              <Check className="h-3 w-3" /> Erledigt
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="lex-card p-6" data-testid="lex-deadline-calendar">
                <div className="flex items-center gap-2 mb-4">
                  <CalendarDays className="h-4 w-4" style={{ color: '#C5A059' }} />
                  <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-500">Fristenkalender</h2>
                </div>
                <div className="space-y-3">
                  {deadlines.slice(0, 8).map((d, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 border-t border-slate-100 pt-3 first:border-0 first:pt-0">
                      <div>
                        <p className="text-sm text-slate-700">{d.label}</p>
                        <p className="text-xs text-slate-400">{d.client_name}</p>
                      </div>
                      <span className={`lex-mono text-xs font-semibold whitespace-nowrap ${d.status === 'overdue' ? 'text-red-600' : 'text-slate-600'}`}>
                        {fmtDate(d.date)}
                      </span>
                    </div>
                  ))}
                  {deadlines.length === 0 && <p className="text-sm text-slate-400">Keine anstehenden Fristen.</p>}
                </div>
              </div>

              <div className="lex-card p-6" data-testid="lex-anomaly-inbox">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="h-4 w-4" style={{ color: '#C5A059' }} />
                  <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-500">Anomalien-Inbox</h2>
                </div>
                {anomalies.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="lex-anomalies-empty">
                    <ShieldCheck className="h-4 w-4" /> Keine Auffälligkeiten — alles plausibel.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {anomalies.map((a, i) => (
                      <div key={i} className={`p-3 text-sm border-l-2 ${a.severity === 'alert' ? 'border-red-500 bg-red-50/50' : 'border-amber-500 bg-amber-50/50'}`}
                        data-testid={`lex-anomaly-${a.type}`}>
                        <p className="text-slate-700">{a.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{a.client_name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {onboardingClient && (
        <LexOnboardingWizard
          advisorClient={onboardingClient}
          onClose={() => setOnboardingClient(null)}
          onComplete={() => { setOnboardingClient(null); loadAll(); }}
        />
      )}
    </div>
  );
}
